import { Validators } from '@angular/forms';
import { FieldType } from '@shared/components/dynamic-form/models/field';
import { FieldCheckbox } from '@shared/components/dynamic-form/models/field-checkbox';
import { FieldDropdown } from '@shared/components/dynamic-form/models/field-dropdown';
import {
  DynamicChoicesSettings,
  FieldDynamicChoices,
} from '@shared/components/dynamic-form/models/field-dynamic-choices';
import { FieldNumber } from '@shared/components/dynamic-form/models/field-number';
import { FieldRichTextEditor } from '@shared/components/dynamic-form/models/field-rich-text-editor';
import { SectionSummarySettings } from '@shared/components/dynamic-form/models/field-section-summary';
import { StarRatingSettings } from '@shared/components/dynamic-form/models/field-star-rating';
import { FieldTextarea } from '@shared/components/dynamic-form/models/field-textarea';
import { FieldTextbox } from '@shared/components/dynamic-form/models/field-textbox';
import { YesNoRatingSettings } from '@shared/components/dynamic-form/models/field-yes-no-rating';

const NUMBER_OF_RATING_CHOICES = 5;

export enum FIELD_TYPE {
  TEXTBOX = 'textbox',
  CHECKBOX = 'checkbox',
  STAR_RATING = 'star-rating',
  DROPDOWN = 'dropdown',
  TEXTAREA = 'textarea',
  NUMBER = 'number',
  CHOICES_RATING = 'choices-rating',
  YES_NO_RATING = 'yes-no-rating',
  SECTION_SUMMARY = 'section-summary',
  FREE_TEXT = 'free-text',
  RICH_TEXT_EDITOR = 'rich-text-editor',
  DYNAMIC_CHOICES = 'dynamic-choices',
}

export const TEXT_FIELD_DEFINITION: FieldType = {
  label: 'JuryForm.Field.TextInput.Label',
  controlType: FIELD_TYPE.TEXTBOX,
  icon: 'matInputOutline',
  containerClass: 'text-input-comment',
  description: 'JuryForm.Field.TextInput.Desc',
  fieldSettings: {
    label: 'Text Field',
    required: true,
    placeholder: 'Please enter',
  },
  fields: [
    new FieldCheckbox({
      key: 'required',
      settings: { label: 'JuryForm.Validation.Mandatory' },
    }),
    new FieldTextarea({
      key: 'label',
      settings: { label: 'JuryForm.Field.Label' },
      validatorOrOpts: Validators.required,
    }),
    new FieldTextbox({
      key: 'placeholder',
      settings: { label: 'Placeholder' },
    }),
  ],
};

export const STAR_RATING_DEFINITION: FieldType = {
  label: 'JuryForm.Field.StarRating.Label',
  controlType: FIELD_TYPE.STAR_RATING,
  icon: 'matStar',
  containerClass: 'star-rating',
  description: 'JuryForm.Field.StarRating.Desc',
  fieldSettings: {
    label: 'Star Rating',
    required: true,
    stars: '5',
    weight: 1,
  } as StarRatingSettings,
  fields: [
    new FieldCheckbox({
      key: 'required',
      settings: { label: 'JuryForm.Validation.Mandatory' },
    }),
    new FieldTextarea({
      key: 'label',
      settings: { label: 'JuryForm.Field.RatingDescription' },
      validatorOrOpts: Validators.required,
    }),
    new FieldDropdown(
      {
        key: 'stars',
        settings: { label: 'JuryForm.Field.NumberOfStars' },
      },
      [
        { codeId: '2', value: '2' },
        { codeId: '3', value: '3' },
        { codeId: '4', value: '4' },
        { codeId: '5', value: '5' },
        { codeId: '6', value: '6' },
        { codeId: '7', value: '7' },
        { codeId: '8', value: '8' },
        { codeId: '9', value: '9' },
        { codeId: '10', value: '10' },
      ]
    ),
    new FieldNumber({
      key: 'weight',
      settings: { label: 'JuryForm.Field.Weight' },
      validatorOrOpts: [Validators.required, Validators.min(1)],
    }),
  ],
};

export const CHOICES_RATING_DEFINITION: FieldType = {
  label: 'JuryForm.Field.ChoicesRating.Label',
  controlType: FIELD_TYPE.CHOICES_RATING,
  icon: 'matListAlt',
  containerClass: 'choices-rating',
  description: 'JuryForm.Field.ChoicesRating.Desc',
  fieldSettings: {
    label: 'Choices Rating',
    required: true,
    choice: {
      choices: NUMBER_OF_RATING_CHOICES,
      selections: [...new Array(NUMBER_OF_RATING_CHOICES)].map((_, i) => ({
        score: i + 1,
        label: 'Choice ' + (i + 1),
      })),
    },
    weight: 1,
  } as DynamicChoicesSettings,
  fields: [
    new FieldCheckbox({
      key: 'required',
      settings: { label: 'JuryForm.Validation.Mandatory' },
    }),
    new FieldTextarea({
      key: 'label',
      settings: { label: 'JuryForm.Field.RatingDescription' },
      validatorOrOpts: [Validators.required],
    }),
    new FieldNumber({
      key: 'weight',
      settings: { label: 'JuryForm.Field.Weight' },
      validatorOrOpts: [Validators.required, Validators.min(1)],
    }),
    new FieldDynamicChoices({
      key: 'choice',
    }),
  ],
};

export const YES_NO_RATING_DEFINITION: FieldType = {
  label: 'JuryForm.Field.YesNoRating.Label',
  controlType: FIELD_TYPE.YES_NO_RATING,
  icon: 'matCheckBox',
  containerClass: 'yes-no-rating',
  description: 'JuryForm.Field.YesNoRating.Desc',
  fieldSettings: {
    label: 'Yes/No Rating',
    required: true,
    weight: 1,
  } as YesNoRatingSettings,
  fields: [
    new FieldCheckbox({
      key: 'required',
      settings: { label: 'JuryForm.Validation.Mandatory' },
    }),
    new FieldTextarea({
      key: 'label',
      settings: { label: 'JuryForm.Field.RatingDescription' },
      validatorOrOpts: Validators.required,
    }),
    new FieldNumber({
      key: 'weight',
      settings: { label: 'JuryForm.Field.Weight' },
      validatorOrOpts: [Validators.required, Validators.min(1)],
    }),
  ],
};

export const SECTION_SUMMARY_DEFINITION: FieldType = {
  label: 'JuryForm.Field.SectionSummary.Label',
  controlType: FIELD_TYPE.SECTION_SUMMARY,
  icon: 'matFunctions',
  containerClass: 'section-summary',
  description: 'JuryForm.Field.SectionSummary.Desc',
  fieldSettings: {
    label: 'Section Summary',
    weight: 1,
  } as SectionSummarySettings,
  fields: [
    new FieldTextarea({
      key: 'label',
      settings: { label: 'JuryForm.Field.SectionSummary.Title' },
      validatorOrOpts: Validators.required,
    }),
    new FieldNumber({
      key: 'weight',
      settings: { label: 'JuryForm.Field.Weight' },
      validatorOrOpts: [Validators.min(1), Validators.required],
    }),
  ],
};

export const FREE_TEXT_DEFINITION: FieldType = {
  label: 'JuryForm.Field.FreeText.Label',
  controlType: FIELD_TYPE.FREE_TEXT,
  icon: 'matTextSnippet',
  containerClass: 'free-text',
  description: 'JuryForm.Field.FreeText.Desc',
  fieldSettings: {
    label:
      'Please provide more details, context or helpful information by editting this element.',
  },
  fields: [
    new FieldRichTextEditor({
      key: 'label',
      settings: {
        label:
          'Please provide more details, context or helpful information by editting this element.',
      },
      validatorOrOpts: Validators.required,
    }),
  ],
};

export const DISABLED_FIELDS_AFTER_RATING_STARTS = [
  'weight',
  'choice',
  'required',
  'stars',
];
